import { cn } from "@/lib/cn";

/**
 * The faint rule grid used on every panel band.
 *
 * Enough texture that a full-bleed near-black band does not read as an empty
 * colour block, faint enough that it never competes with type sitting on it.
 *
 * **A raw `rgba()` rather than a token, on purpose.** This is a 7%-opacity white
 * hairline that only ever exists on the panel surface, and promoting it to the
 * shared token file would put a value in Flutter's palette that no Flutter
 * screen can use. It lives here, once, which is the part that actually matters -
 * the same block used to be copy-pasted into `AuthLayout` as well.
 *
 * `origin` moves the focal point of the mask, not the grid. The band reads as
 * lit from wherever the eye enters it: the marketing hero is entered from the
 * top centre, the auth brand panel from the top left where the wordmark sits.
 * Fading from the wrong corner leaves the texture strongest behind the headline,
 * which is the one place it must not be.
 */

const ORIGINS = {
  top: "radial-gradient(120% 100% at 50% 0%, #000 30%, transparent 78%)",
  "top-left": "radial-gradient(110% 90% at 15% 10%, #000 25%, transparent 75%)",
} as const;

const RULES =
  "repeating-linear-gradient(0deg, rgba(255,255,255,.055) 0 1px, transparent 1px 56px)," +
  "repeating-linear-gradient(90deg, rgba(255,255,255,.055) 0 1px, transparent 1px 56px)";

export function PanelGrid({
  origin = "top",
  className,
}: {
  origin?: keyof typeof ORIGINS;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cn("panel-grid", className)}
      style={{ backgroundImage: RULES, maskImage: ORIGINS[origin] }}
    />
  );
}
